import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Search, Plus, Edit, Truck, Wrench, AlertTriangle, CheckCircle, Megaphone } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  getEquipment, 
  createEquipment, 
  updateEquipment, 
  getEmployees,
  createReplacementRequest,
  getReplacementRequests,
  updateReplacementRequest
} from '../../services/api';

const UNASSIGNED_VALUE = '__unassigned__';

const REPORT_KINDS = {
  broken: { label: 'Brisé', className: 'bg-red-100 text-red-700' },
  missing: { label: 'Manquant', className: 'bg-amber-100 text-amber-800' },
  other: { label: 'Autre', className: 'bg-slate-100 text-slate-700' },
};

const REQUEST_STATUS = {
  pending: { label: 'En attente', className: 'badge-pending' },
  approved: { label: 'Approuvé', className: 'badge-completed' },
  resolved: { label: 'Réglé', className: 'badge-completed' },
  rejected: { label: 'Refusé', className: 'badge-cancelled' },
};

const EMPTY_REPORT = { kind: 'broken', equipment_name: '', reason: '', photo_url: '' };

export const EquipmentPage = () => {
  const { user, can } = useAuth();
  const canManage = can('equipment.manage');
  const canSeeEquipment = can('equipment.view');
  const canRequestReplacement = can('replacement_requests.create');
  const canHandleReplacements = can('replacement_requests.manage');
  const [equipment, setEquipment] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [replacementRequests, setReplacementRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [replaceData, setReplaceData] = useState({ reason: '', photo_url: '' });
  const [reportOpen, setReportOpen] = useState(false);
  const [reportData, setReportData] = useState(EMPTY_REPORT);
  // Signalement d'origine quand l'item est créé à partir de celui-ci.
  const [sourceReport, setSourceReport] = useState(null);
  const [activeTab, setActiveTab] = useState(canSeeEquipment ? 'equipment' : 'requests');

  const fetchData = useCallback(async () => {
    try {
      const [equipRes, empRes, reqRes] = await Promise.all([
        canSeeEquipment ? getEquipment() : Promise.resolve({ data: [] }),
        canManage ? getEmployees() : Promise.resolve({ data: [] }),
        canRequestReplacement || canHandleReplacements ? getReplacementRequests() : Promise.resolve({ data: [] })
      ]);
      const equipmentData = Array.isArray(equipRes.data) ? equipRes.data : [];
      const employeeData = Array.isArray(empRes.data) ? empRes.data : [];
      const requestData = Array.isArray(reqRes.data) ? reqRes.data : [];

      setEquipment(equipmentData);
      setEmployees(employeeData.filter(e => e.role !== 'admin'));
      setReplacementRequests(requestData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [canSeeEquipment, canManage, canRequestReplacement, canHandleReplacements]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedEquipment) {
        await updateEquipment(selectedEquipment.id, formData);
        toast.success('Équipement mis à jour');
      } else {
        await createEquipment(formData);
        if (sourceReport) {
          await updateReplacementRequest(sourceReport.id, { status: 'resolved' });
          toast.success('Équipement ajouté et signalement réglé');
        } else {
          toast.success('Équipement ajouté');
        }
      }
      setFormOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleStatusUpdate = async (equipId, status, assignedTo = null) => {
    try {
      const updateData = { status };
      if (assignedTo !== undefined) updateData.assigned_to = assignedTo;
      await updateEquipment(equipId, updateData);
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleReplacementRequest = async (e) => {
    e.preventDefault();
    try {
      await createReplacementRequest({
        equipment_id: selectedEquipment.id,
        equipment_name: selectedEquipment.name,
        ...replaceData
      });
      toast.success('Demande de remplacement envoyée');
      setReplaceOpen(false);
      setReplaceData({ reason: '', photo_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const handleReport = async (e) => {
    e.preventDefault();
    try {
      await createReplacementRequest({
        kind: reportData.kind,
        equipment_name: reportData.equipment_name,
        reason: reportData.reason,
        photo_url: reportData.photo_url || null,
      });
      toast.success('Signalement envoyé');
      setReportOpen(false);
      setReportData(EMPTY_REPORT);
      setActiveTab('requests');
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const createFromReport = (request) => {
    setSelectedEquipment(null);
    setSourceReport(request);
    setFormData({ name: request.equipment_name, description: request.reason || '' });
    setFormOpen(true);
  };

  const handleReplacementAction = async (requestId, status) => {
    try {
      await updateReplacementRequest(requestId, { status });
      toast.success(REQUEST_STATUS[status]?.label || 'Demande mise à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '' });
    setSelectedEquipment(null);
    setSourceReport(null);
  };

  const openEditForm = (equip) => {
    setFormData({ name: equip.name, description: equip.description || '' });
    setSelectedEquipment(equip);
    setFormOpen(true);
  };

  const filteredEquipment = equipment.filter(equip => {
    const matchesSearch = equip.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || equip.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const badges = {
      available: 'bg-green-100 text-green-700',
      in_use: 'bg-blue-100 text-blue-700',
      in_repair: 'bg-yellow-100 text-yellow-700',
      broken: 'bg-red-100 text-red-700'
    };
    return badges[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      available: 'Disponible',
      in_use: 'En utilisation',
      in_repair: 'En réparation',
      broken: 'Brisé'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    const icons = {
      available: CheckCircle,
      in_use: Truck,
      in_repair: Wrench,
      broken: AlertTriangle
    };
    return icons[status] || CheckCircle;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#00b4d8] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="equipment-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1a3a5c]">Matériel & Équipements</h1>
          <p className="text-slate-500">Gérez le matériel de l'entreprise</p>
        </div>
        <div className="flex flex-wrap gap-3">
        {canRequestReplacement && (
          <Button
            onClick={() => {
              setReportData(EMPTY_REPORT);
              setReportOpen(true);
            }}
            variant="outline"
            className="rounded-full"
            data-testid="report-equipment-btn"
          >
            <Megaphone className="w-4 h-4 mr-2" />
            Signaler un manque ou un bris
          </Button>
        )}
        {canManage && (
          <Button 
            onClick={() => {
              resetForm();
              setFormOpen(true);
            }}
            className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8]"
            data-testid="add-equipment-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un équipement
          </Button>
        )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {canSeeEquipment && (
        <Button
          variant={activeTab === 'equipment' ? 'default' : 'outline'}
          onClick={() => setActiveTab('equipment')}
          className={activeTab === 'equipment' ? 'bg-[#00b4d8]' : ''}
        >
          Équipements
        </Button>
        )}
        <Button
          variant={activeTab === 'requests' ? 'default' : 'outline'}
          onClick={() => setActiveTab('requests')}
          className={activeTab === 'requests' ? 'bg-[#00b4d8]' : ''}
        >
          Signalements
          {replacementRequests.filter(r => r.status === 'pending').length > 0 && (
            <span className="ml-2 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
              {replacementRequests.filter(r => r.status === 'pending').length}
            </span>
          )}
        </Button>
      </div>

      {activeTab === 'equipment' && (
        <>
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="available">Disponible</SelectItem>
                    <SelectItem value="in_use">En utilisation</SelectItem>
                    <SelectItem value="in_repair">En réparation</SelectItem>
                    <SelectItem value="broken">Brisé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Equipment Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEquipment.map((equip) => {
              const StatusIcon = getStatusIcon(equip.status);
              return (
                <Card key={equip.id} className="card-hover" data-testid={`equipment-card-${equip.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getStatusBadge(equip.status)}`}>
                          <StatusIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#1a3a5c]">{equip.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusBadge(equip.status)}`}>
                            {getStatusLabel(equip.status)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {equip.description && (
                      <p className="text-sm text-slate-500 mb-4">{equip.description}</p>
                    )}

                    {equip.assigned_to_name && (
                      <p className="text-sm text-slate-600 mb-4">
                        Assigné à: <span className="font-medium">{equip.assigned_to_name}</span>
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2 pt-4 border-t">
                      {canManage ? (
                        <>
                          <Button variant="outline" size="sm" onClick={() => openEditForm(equip)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Select 
                            value={equip.status}
                            onValueChange={(value) => handleStatusUpdate(equip.id, value)}
                          >
                            <SelectTrigger className="h-8 w-32 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="available">Disponible</SelectItem>
                              <SelectItem value="in_use">En utilisation</SelectItem>
                              <SelectItem value="in_repair">En réparation</SelectItem>
                              <SelectItem value="broken">Brisé</SelectItem>
                            </SelectContent>
                          </Select>
                          {employees.length > 0 && (
                            <Select 
                              value={equip.assigned_to || UNASSIGNED_VALUE}
                              onValueChange={(value) => {
                                const isUnassigned = value === UNASSIGNED_VALUE;
                                handleStatusUpdate(
                                  equip.id,
                                  isUnassigned ? 'available' : 'in_use',
                                  isUnassigned ? null : value
                                );
                              }}
                            >
                              <SelectTrigger className="h-8 w-40 text-xs">
                                <SelectValue placeholder="Assigner" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={UNASSIGNED_VALUE}>Non assigné</SelectItem>
                                {employees.map(emp => (
                                  <SelectItem key={emp.id} value={emp.id}>
                                    {emp.first_name} {emp.last_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </>
                      ) : (
                        canRequestReplacement && equip.assigned_to === user?.id && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setSelectedEquipment(equip);
                              setReplaceOpen(true);
                            }}
                          >
                            Demander remplacement
                          </Button>
                        )
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {filteredEquipment.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <p>Aucun équipement trouvé</p>
            </div>
          )}
        </>
      )}

      {activeTab === 'requests' && (
        <Card>
          <CardContent className="p-0">
            {replacementRequests.length > 0 ? (
              <div className="divide-y">
                {replacementRequests.map((request) => {
                  const kind = REPORT_KINDS[request.kind] || REPORT_KINDS.broken;
                  const state = REQUEST_STATUS[request.status] || REQUEST_STATUS.pending;
                  const freeReport = !request.equipment_id;
                  const pending = request.status === 'pending';
                  return (
                  <div key={request.id} className="p-4" data-testid={`report-${request.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-semibold text-[#1a3a5c]">{request.equipment_name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${kind.className}`}>{kind.label}</span>
                          {freeReport && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">Hors inventaire</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          Par : {request.requested_by_name}
                          {request.created_at && ` · ${new Date(request.created_at).toLocaleDateString('fr-CA')}`}
                        </p>
                        <p className="text-sm text-slate-600 mt-2 whitespace-pre-line">{request.reason}</p>
                        {request.photo_url && /^https?:\/\//.test(request.photo_url) && (
                          <a href={request.photo_url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#00b4d8] hover:underline">
                            Voir la photo
                          </a>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 shrink-0">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${state.className}`}>
                          {state.label}
                        </span>
                        {canHandleReplacements && pending && (
                          <>
                            {freeReport ? (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleReplacementAction(request.id, 'resolved')}
                                  data-testid={`resolve-report-${request.id}`}
                                >
                                  Réglé
                                </Button>
                                {canManage && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => createFromReport(request)}
                                    data-testid={`create-from-report-${request.id}`}
                                  >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Créer l'item
                                  </Button>
                                )}
                              </>
                            ) : (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleReplacementAction(request.id, 'approved')}
                              >
                                Approuver
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600"
                              onClick={() => handleReplacementAction(request.id, 'rejected')}
                            >
                              Refuser
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <p>Aucun signalement</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Form Dialog */}
      <Dialog open={formOpen} onOpenChange={(open) => {
        setFormOpen(open);
        if (!open) setSourceReport(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedEquipment ? 'Modifier' : 'Ajouter'} un équipement</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nom *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Visseuse DeWalt"
                required
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Numéro de série, caractéristiques..."
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#00b4d8] hover:bg-[#009bb8]">
                {selectedEquipment ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Signalement libre */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Signaler un manque ou un bris</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReport} className="space-y-4" data-testid="report-form">
            <div>
              <Label>Type *</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {Object.entries(REPORT_KINDS).map(([value, kind]) => (
                  <Button
                    key={value}
                    type="button"
                    variant={reportData.kind === value ? 'default' : 'outline'}
                    className={reportData.kind === value ? 'bg-[#1a3a5c] hover:bg-[#1a3a5c]' : ''}
                    onClick={() => setReportData({ ...reportData, kind: value })}
                  >
                    {kind.label}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Matériel concerné *</Label>
              <Input
                value={reportData.equipment_name}
                onChange={(e) => setReportData({ ...reportData, equipment_name: e.target.value })}
                placeholder="Ex. : pince à sertir, rallonge, échelle 8 pi"
                maxLength={120}
                required
                data-testid="report-name"
              />
            </div>
            <div>
              <Label>Détails *</Label>
              <Textarea
                value={reportData.reason}
                onChange={(e) => setReportData({ ...reportData, reason: e.target.value })}
                placeholder="Ce qui manque ou ce qui est brisé, où, depuis quand..."
                maxLength={2000}
                required
                data-testid="report-reason"
              />
            </div>
            <div>
              <Label>Lien d'une photo (optionnel)</Label>
              <Input
                value={reportData.photo_url}
                onChange={(e) => setReportData({ ...reportData, photo_url: e.target.value })}
                placeholder="https://..."
                maxLength={500}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setReportOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#00b4d8] hover:bg-[#009bb8]" data-testid="report-submit">
                Envoyer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Replacement Request Dialog */}
      <Dialog open={replaceOpen} onOpenChange={setReplaceOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Demande de remplacement</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReplacementRequest} className="space-y-4">
            <p className="text-slate-600">
              Équipement: <span className="font-medium">{selectedEquipment?.name}</span>
            </p>
            <div>
              <Label>Raison du remplacement *</Label>
              <Textarea
                value={replaceData.reason}
                onChange={(e) => setReplaceData({ ...replaceData, reason: e.target.value })}
                placeholder="Décrivez le problème..."
                required
              />
            </div>
            <div>
              <Label>URL de la photo (optionnel)</Label>
              <Input
                value={replaceData.photo_url}
                onChange={(e) => setReplaceData({ ...replaceData, photo_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setReplaceOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#00b4d8] hover:bg-[#009bb8]">
                Envoyer la demande
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EquipmentPage;
